import React, { useRef, useState } from "react";
import { Alert, Button, ProgressBar, Table } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import * as XLSX from "xlsx";
import { rmcService } from "../service/rmcService";
import "./RMCImportDrawer.css";

interface RMCImportError {
  columnName: string;
  errorMessage: string;
  columnIndex: number;
}

interface RMCImportDto {
  rowNumber: number;
  name: string;
  cityName?: string;
  type?: string;
  description?: string;
  address?: string;
  zipCode?: string;
  code?: string;
  errors: RMCImportError[];
  hasErrors: boolean;
}

interface RMCImportResult {
  totalRows: number;
  successCount: number;
  errorCount: number;
  errorRows: RMCImportDto[];
  hasErrors: boolean;
  message: string;
  errorFileBase64?: string;
  errorFileName?: string;
}

interface RMCImportDrawerProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
}

const RMCImportDrawer: React.FC<RMCImportDrawerProps> = ({
  show,
  onHide,
  onSuccess,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importResult, setImportResult] = useState<RMCImportResult | null>(
    null
  );
  const [error, setError] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      if (
        !validTypes.includes(file.type) &&
        !file.name.match(/\.(xlsx|xls)$/i)
      ) {
        setError("Please select a valid Excel file (.xlsx or .xls)");
        return;
      }

      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError("File size must be less than 10MB");
        return;
      }

      setSelectedFile(file);
      setError("");
      setImportResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError("Please select a file first");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError("");
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      const response = await rmcService.bulkImport(formData);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.success && response.data) {
        setImportResult(response.data);

        if (!response.data.hasErrors) {
          // Success - show success message and refresh grid
          setTimeout(() => {
            onSuccess();
            handleClose();
          }, 2000);
        }
      } else {
        setError(response.message || "Import failed. Please try again.");
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "An error occurred during import. Please try again."
      );
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadErrorFile = () => {
    if (importResult?.errorFileBase64 && importResult?.errorFileName) {
      // Convert base64 to blob and download
      const byteCharacters = atob(importResult.errorFileBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = importResult.errorFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  };

  const handleDownloadTemplate = () => {
    // Create Excel template with headers
    const template = [
      [
        "Name",
        "City Name",
        "Type",
        "Description",
        "Address",
        "Zip Code",
        "Code",
      ],
      [
        "Example RMC 1",
        "New York",
        "RMC",
        "Sample description",
        "123 Main St",
        "10001",
        "RMC001",
      ],
      [
        "Example RMC 2",
        "Los Angeles",
        "Aggregator",
        "Another description",
        "456 Oak Ave",
        "90001",
        "AGG001",
      ],
    ];

    const ws = XLSX.utils.aoa_to_sheet(template);

    // Set column widths
    ws["!cols"] = [
      { wch: 20 }, // Name
      { wch: 20 }, // City Name
      { wch: 15 }, // Type
      { wch: 40 }, // Description
      { wch: 30 }, // Address
      { wch: 10 }, // Zip Code
      { wch: 10 }, // Code
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "RMC Import Template");
    XLSX.writeFile(wb, "RMC_Import_Template.xlsx");
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploading(false);
    setUploadProgress(0);
    setImportResult(null);
    setError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onHide();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError("");
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <>
      {/* Overlay */}
      {show && <div className="rmc-drawer-overlay" onClick={handleClose} />}

      {/* Drawer */}
      <div className={`rmc-import-drawer ${show ? "show" : ""}`}>
        {/* Header */}
        <div className="rmc-drawer-header">
          <div className="rmc-drawer-title">
            <Icon.FileEarmarkArrowUp size={20} className="me-2 text-white" />
            <h4 className="mb-0">Import RMCs from Excel</h4>
          </div>
          <button
            className="rmc-drawer-close"
            onClick={handleClose}
            disabled={uploading}
            aria-label="Close"
          >
            <Icon.X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="rmc-drawer-body">
          {/* Download Template Section */}
          <div className="rmc-drawer-section">
            <div className="rmc-section-header">
              <Icon.Download size={18} className="me-2 text-success" />
              <h6 className="mb-0">Download Excel Template</h6>
            </div>
            <p className="rmc-section-description">
              Download the template to ensure your data is in the correct
              format.
            </p>
            <Button
              variant="outline-success"
              size="sm"
              onClick={handleDownloadTemplate}
              className="rmc-modern-btn-outline"
            >
              <Icon.FileEarmarkSpreadsheet className="me-1" size={15} />
              Download Template
            </Button>
          </div>

          {/* File Upload Section */}
          <div className="rmc-drawer-section">
            <div className="rmc-section-header">
              <Icon.CloudUpload size={18} className="me-2 text-success" />
              <h6 className="mb-0">Upload Excel File</h6>
            </div>
            <div className="rmc-file-upload-container">
              <input
                ref={fileInputRef}
                type="file"
                className="form-control rmc-file-input"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                disabled={uploading}
                aria-label="Select Excel file to upload"
              />
              {selectedFile && !uploading && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleRemoveFile}
                  title="Remove file"
                  className="rmc-remove-file-btn"
                >
                  <Icon.XCircle size={16} />
                </Button>
              )}
            </div>
            {selectedFile && (
              <div className="rmc-file-info">
                <Icon.FileEarmark className="me-2" size={14} />
                <span>
                  {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)}{" "}
                  KB)
                </span>
              </div>
            )}
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="rmc-drawer-section">
              <ProgressBar
                now={uploadProgress}
                label={`${uploadProgress}%`}
                animated
                striped
                className="rmc-progress-bar"
              />
            </div>
          )}

          {/* Error Message */}
          {error && (
            <Alert
              variant="danger"
              dismissible
              onClose={() => setError("")}
              className="rmc-alert-danger"
            >
              <Icon.ExclamationTriangle className="me-2" />
              {error}
            </Alert>
          )}

          {/* Import Result */}
          {importResult && (
            <div className="rmc-drawer-section">
              {importResult.hasErrors ? (
                <Alert variant="warning" className="rmc-alert-warning">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <h6 className="rmc-alert-title">
                        <Icon.ExclamationTriangle className="me-2" />
                        Validation Errors Found
                      </h6>
                      <p className="mb-2">{importResult.message}</p>
                      <ul className="rmc-result-list">
                        <li>Total Rows: {importResult.totalRows}</li>
                        <li>Rows with Errors: {importResult.errorCount}</li>
                      </ul>
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={handleDownloadErrorFile}
                      className="rmc-modern-btn-outline"
                    >
                      <Icon.Download className="me-2" size={16} />
                      Download Errors
                    </Button>
                  </div>

                  {/* Error Details Table */}
                  {importResult.errorRows.length > 0 && (
                    <div className="rmc-error-table-container">
                      <h6 className="mb-3">Error Details (First 10 rows):</h6>
                      <Table
                        striped
                        bordered
                        hover
                        size="sm"
                        className="rmc-error-table"
                      >
                        <thead>
                          <tr>
                            <th>Row</th>
                            <th>Errors</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importResult.errorRows.slice(0, 10).map((row) => (
                            <tr key={row.rowNumber}>
                              <td>{row.rowNumber}</td>
                              <td>
                                <ul className="rmc-error-list">
                                  {row.errors.map((error, idx) => (
                                    <li key={idx}>
                                      <strong>{error.columnName}:</strong>{" "}
                                      {error.errorMessage}
                                    </li>
                                  ))}
                                </ul>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      {importResult.errorRows.length > 10 && (
                        <p className="rmc-more-errors">
                          ... and {importResult.errorRows.length - 10} more
                          error(s). Download the error file for complete
                          details.
                        </p>
                      )}
                    </div>
                  )}
                </Alert>
              ) : (
                <Alert variant="success" className="rmc-alert-success">
                  <Icon.CheckCircle className="me-2" size={18} />
                  <strong>Success!</strong> {importResult.message}
                  <ul className="rmc-result-list mt-2">
                    <li>Total Rows: {importResult.totalRows}</li>
                    <li>Successfully Imported: {importResult.successCount}</li>
                  </ul>
                </Alert>
              )}
            </div>
          )}

          {/* Instructions */}
          <div className="rmc-drawer-section rmc-instructions-section">
            <div className="rmc-section-header">
              <Icon.InfoCircle size={18} className="me-2 text-success" />
              <h6 className="mb-0">Import Instructions</h6>
            </div>
            <ul className="rmc-instructions-list">
              <li>
                <strong>Name:</strong> Required. RMC name (max 100 characters,
                alphabets only)
              </li>
              <li>
                <strong>City Name:</strong> Required. Must match existing city
                in system
              </li>
              <li>
                <strong>Type:</strong> Required. Must be either "RMC" or
                "Aggregator"
              </li>
              <li>
                <strong>Description:</strong> Optional (max 2000 characters)
              </li>
              <li>
                <strong>Address:</strong> Optional (max 500 characters)
              </li>
              <li>
                <strong>Zip Code:</strong> Optional (max 10 characters)
              </li>
              <li>
                <strong>Code:</strong> Optional (max 20 characters)
              </li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="rmc-drawer-footer">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={uploading}
            className="rmc-footer-btn-secondary"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className="rmc-footer-btn-primary btn-success"
          >
            {uploading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Uploading...
              </>
            ) : (
              <>
                <Icon.Upload className="me-2" size={16} />
                Upload & Import
              </>
            )}
          </Button>
        </div>
      </div>
    </>
  );
};

export default RMCImportDrawer;
